<template>
  <div>
    <section v-if="hasSomeButtonToShow" class="services-section">
      <Heading :level="3" class="trade-position-heading__title">
        Serviços
      </Heading>
      <div class="services-section__list">
        <template v-for="button in props.serviceButtons">
          <ButtonBoxLaunch
            v-if="isButtonVisible"
            :key="button.label"
            :disabled="!button.enabled"
            :label="button.label"
            :icon="button.icon"
            :badge-label="button.chipLabel"
            :class="getChipCustomStyle(button.chipStyle)"
            @click="handleClick(button)"
          />
        </template>
      </div>
    </section>
    <RequestBrokerModal
      :is-open="showHomeBrokerModal"
      :broker-status="homeBrokerStatus"
      @close="showHomeBrokerModal = false"
      @update-status="updateHomeBrokerStatus"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import { ButtonBoxLaunch, Heading } from '@warrenbrasil/nebraska-web';
import { RequestBrokerModal } from './components';

import { TradeButton } from '@/modules/trade/services/buttons';
import { TradeHomeBrokerStatus } from '@/modules/trade/services/account';

interface Emits {
  (event: 'open-home-broker'): void;
}

const props = defineProps<{
  serviceButtons: TradeButton[];
}>();

const emits = defineEmits<Emits>();

const router = useRouter();

const showHomeBrokerModal = ref(false);
const homeBrokerStatus = ref('');

const isButtonVisible = (button: TradeButton) => {
  if (button.name === 'marginAllocation') {
    return true;
  }
  return button.enabled;
};

const hasSomeButtonToShow = computed(() =>
  props.serviceButtons?.some((button: TradeButton) => button.enabled)
);

function getChipCustomStyle(chipStyle: string | undefined) {
  if (!chipStyle) return;
  return `services-section__chip--${chipStyle}`;
}

function handleClick(button: TradeButton) {
  switch (button.name) {
    case 'securitiesLendingOverview':
      router.push({ name: button.name, query: { group: 'btcPosition' } });
      break;

    case 'homeBroker':
      handleHomeBrokerClick(button.status as string);
      break;

    default:
      router.push({ name: button.name });
      break;
  }
}

function handleHomeBrokerClick(status: string) {
  if (status === TradeHomeBrokerStatus.Enabled) {
    emits('open-home-broker');
    return;
  }

  showHomeBrokerModal.value = true;
  homeBrokerStatus.value = status;
}

async function updateHomeBrokerStatus() {
  showHomeBrokerModal.value = false;
}
</script>

<style lang="less" scoped>
.services-section {
  margin-bottom: @size-spacing-x800;

  &__list {
    margin-top: @size-spacing-x400;
    display: flex;
    gap: @size-spacing-x400;
    flex-flow: row wrap;
  }

  &__chip--success {
    :deep(.badge-label) {
      color: @status-positive;
      background-color: @status-positive-background;
    }
  }

  &__chip--negative {
    :deep(.badge-label) {
      color: @status-negative;
      background-color: @status-negative-background;
    }
  }
}
</style>
